import { Component, Input, Output, EventEmitter, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Policy, PolicyType } from '../../../core/services/policy-flow';

type PolicyFilter = 'pending' | 'approved' | 'all';

// Each policy_type gets a distinct, legible chip color.
const TYPE_COLORS: Record<PolicyType, { bg: string; fg: string }> = {
  eligibility: { bg: '#E0F2FE', fg: '#075985' },
  approval: { bg: '#DCFCE7', fg: '#166534' },
  limit: { bg: '#FEF3C7', fg: '#92400E' },
  obligation: { bg: '#EDE9FE', fg: '#5B21B6' },
  prohibition: { bg: '#FEE2E2', fg: '#991B1B' },
  constraint: { bg: '#E2E8F0', fg: '#334155' },
};

@Component({
  selector: 'app-policy-review-table',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './policy-review-table.html',
  styleUrl: './policy-review-table.css',
})
export class PolicyReviewTable {
  @Input() set policies(value: Policy[]) {
    this._policies.set(value ?? []);
  }
  /** Policy ids with a setReview call currently in flight (row disabled + spinner). */
  @Input() set pendingIds(value: Set<string>) {
    this._pendingIds.set(value ?? new Set<string>());
  }

  @Output() review = new EventEmitter<{ id: string; review: boolean }>();

  private _policies = signal<Policy[]>([]);
  private _pendingIds = signal<Set<string>>(new Set<string>());

  filter = signal<PolicyFilter>('pending');
  query = signal('');
  private expanded = signal<Set<string>>(new Set<string>());

  readonly filters: PolicyFilter[] = ['pending', 'approved', 'all'];

  pendingCount = computed(() => this._policies().filter(p => !p.review).length);
  approvedCount = computed(() => this._policies().filter(p => p.review).length);
  totalCount = computed(() => this._policies().length);

  filtered = computed<Policy[]>(() => {
    const f = this.filter();
    const q = this.query().trim().toLowerCase();
    return this._policies().filter(p => {
      if (f === 'pending' && p.review) return false;
      if (f === 'approved' && !p.review) return false;
      if (q && !(p.statement.toLowerCase().includes(q) || p.policy_type.toLowerCase().includes(q))) {
        return false;
      }
      return true;
    });
  });

  setFilter(f: PolicyFilter) {
    this.filter.set(f);
  }

  typeStyle(type: PolicyType) {
    return TYPE_COLORS[type] ?? TYPE_COLORS.constraint;
  }

  isExpanded(id: string): boolean {
    return this.expanded().has(id);
  }

  toggleEvidence(id: string) {
    this.expanded.update(set => {
      const next = new Set(set);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  isPending(id: string): boolean {
    return this._pendingIds().has(id);
  }

  onToggleReview(p: Policy) {
    if (this.isPending(p.policy_id)) return;
    this.review.emit({ id: p.policy_id, review: !p.review });
  }

  trackById(_: number, p: Policy) {
    return p.policy_id;
  }
}
